import React from 'react';

export interface LoadingStep {
  label: string;
  status: 'pending' | 'active' | 'completed';
}

export interface LoadingStateProps {
  title: string;
  message?: string;
  steps?: LoadingStep[];
  variant?: 'spinner' | 'dots' | 'progress';
  showEstimate?: boolean;
  estimateText?: string;
  darkMode?: boolean; // kept for backwards compatibility but not used
}

const LoadingState: React.FC<LoadingStateProps> = ({
  title,
  message,
  steps,
  variant = 'spinner',
  showEstimate = true,
  estimateText = 'This may take a few seconds...',
}) => {
  const renderSpinner = () => (
    <div className="relative w-16 h-16">
      <div className="w-16 h-16 border-4 rounded-full border-blue-200 dark:border-gray-700"></div>
      <div className="w-16 h-16 border-4 rounded-full border-t-transparent border-blue-500 dark:border-blue-400 animate-spin absolute top-0 left-0"></div>
    </div>
  );

  const renderDots = () => (
    <div className="flex items-center gap-2">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="w-4 h-4 rounded-full animate-bounce bg-blue-500 dark:bg-blue-400"
          style={{ animationDelay: `${i * 150}ms` }}
        />
      ))}
    </div>
  );

  const renderProgress = () => (
    <div className="w-48 h-2 rounded-full overflow-hidden bg-blue-200 dark:bg-gray-700">
      <div className="h-full rounded-full animate-pulse w-full origin-left scale-x-50 animate-[loading_1.5s_ease-in-out_infinite] bg-blue-500 dark:bg-blue-400" />
    </div>
  );

  const renderLoader = () => {
    switch (variant) {
      case 'dots':
        return renderDots();
      case 'progress':
        return renderProgress();
      default:
        return renderSpinner();
    }
  };

  return (
    <div className="flex flex-col items-center justify-center p-8 animate-fadeIn">
      {/* Loader */}
      <div className="mb-6">{renderLoader()}</div>

      {/* Title */}
      <h3 className="text-xl font-semibold mb-2 text-gray-800 dark:text-gray-100">{title}</h3>

      {/* Message */}
      {message && <p className="text-center mb-4 text-gray-600 dark:text-gray-400">{message}</p>}

      {/* Steps */}
      {steps && steps.length > 0 && (
        <div className="w-full max-w-xs mt-4">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`flex items-center gap-3 py-2 ${
                index < steps.length - 1 ? 'border-b border-gray-100 dark:border-gray-800' : ''
              }`}
            >
              {/* Step indicator */}
              <div className="flex-shrink-0">
                {step.status === 'completed' ? (
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                ) : step.status === 'active' ? (
                  <div className="w-6 h-6 border-2 rounded-full flex items-center justify-center border-blue-500 dark:border-blue-400">
                    <div className="w-3 h-3 rounded-full animate-pulse bg-blue-500 dark:bg-blue-400" />
                  </div>
                ) : (
                  <div className="w-6 h-6 border-2 rounded-full border-gray-300 dark:border-gray-600" />
                )}
              </div>

              {/* Step label */}
              <span
                className={`text-sm ${
                  step.status === 'completed'
                    ? 'text-green-500'
                    : step.status === 'active'
                    ? 'text-blue-600 dark:text-blue-400 font-medium'
                    : 'text-gray-400 dark:text-gray-500'
                }`}
              >
                {step.label}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Estimate */}
      {showEstimate && (
        <p className="text-sm mt-4 text-gray-400 dark:text-gray-500">{estimateText}</p>
      )}
    </div>
  );
};

export default LoadingState;
